import { collection, doc, getDoc, getDocs, orderBy, query, where } from 'firebase/firestore';

// Aplicaciones de Caser que notifican a PAP. Servicio de estadísticas
// Umbral actual del proceso de alertas: tras 10 intentos fallidos a PAP se genera aviso por mail a Casavi.
const PAP_ALERT_MAX_NOTIFICATION_TRIES = 10;
// Ventana de gracia para alertas recien creadas antes de que checkAlerts decida su camino real.
// Incluye prealertas automaticas, manuales visibles y canceladas antes de consolidarse.
const ALERT_VALIDATION_GRACE_MS = 210000;

const dateValue = (value) => {
    if (!value) return null;
    if (typeof value === 'number') return value;
    if (value instanceof Date) return value.getTime();
    if (value.seconds) return value.seconds * 1000;
    if (typeof value === 'string') {
        const parsed = Date.parse(value);
        return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
}

const serviceMatches = (docService, service) => {
    if (!service) return true;
    const expectedServices = (Array.isArray(service) ? service : [service])
        .filter(Boolean)
        .map(item => `${item}`.toUpperCase());
    const docServices = (Array.isArray(docService) ? docService : [docService])
        .filter(Boolean)
        .map(item => `${item}`.toUpperCase());
    return docServices.some(item => expectedServices.indexOf(item) > -1);
}

const alertStatsKey = row => `${row.alertId || ''}${row.phone || ''}`;

const alertStatsDateFields = {
    alerts: [
        { field: 'alertId', type: 'number' },
        { field: 'stPapAlertAt', type: 'number' },
        { field: 'stPapAlertErrAt', type: 'number' },
        { field: 'stPapAlertRespAt', type: 'number' },
        { field: 'stPapAlertMailAt', type: 'number' },
        { field: 'stPapAlertMailQueuedAt', type: 'number' },
        { field: 'stNoPolAlertMailQueuedAt', type: 'number' },
        { field: 'stNoPolCancelMailQueuedAt', type: 'number' },
        { field: 'stMailQueuedAt', type: 'number' },
        { field: 'notificatedTime', type: 'number' }
    ],
    cancelled: [
        { field: 'alertId', type: 'number' },
        { field: 'cancelId', type: 'number' },
        { field: 'cancelationTime', type: 'number' },
        { field: 'cancelledTime', type: 'number' },
        { field: 'stPapAlertAt', type: 'number' },
        { field: 'stPapAlertErrAt', type: 'number' },
        { field: 'stPapAlertRespAt', type: 'number' },
        { field: 'stPapAlertMailAt', type: 'number' },
        { field: 'stPapAlertMailQueuedAt', type: 'number' },
        { field: 'stPapCancelAt', type: 'number' },
        { field: 'stPapCancelErrAt', type: 'number' },
        { field: 'stPapCancelRespAt', type: 'number' },
        { field: 'stPapCancelMailAt', type: 'number' },
        { field: 'stPapCancelMailQueuedAt', type: 'number' },
        { field: 'stNoPolAlertMailQueuedAt', type: 'number' },
        { field: 'stNoPolCancelMailQueuedAt', type: 'number' },
        { field: 'stMailQueuedAt', type: 'number' },
        { field: 'notificatedTime', type: 'number' }
    ],
    closed: [
        { field: 'alertId', type: 'number' },
        { field: 'closeTime', type: 'timestamp' },
        { field: 'closedTime', type: 'timestamp' },
        { field: 'stPapAlertAt', type: 'number' },
        { field: 'stPapAlertErrAt', type: 'number' },
        { field: 'stPapAlertRespAt', type: 'number' },
        { field: 'stPapAlertMailAt', type: 'number' },
        { field: 'stPapAlertMailQueuedAt', type: 'number' },
        { field: 'stNoPolAlertMailQueuedAt', type: 'number' },
        { field: 'stMailQueuedAt', type: 'number' },
        { field: 'notificatedTime', type: 'number' }
    ]
};

const queryAlertStatsCollection = async (db, { coll, from, to, service }) => {
    const rowsById = new Map();
    const dateFields = alertStatsDateFields[coll] || [{ field: 'alertId', type: 'number' }];

    await Promise.all(dateFields.map(async ({ field, type }) => {
        const fromValue = type === 'timestamp' ? new Date(from) : from;
        const toValue = type === 'timestamp' ? new Date(to) : to;
        const q = query(
            collection(db, coll),
            where(field, '>=', fromValue),
            where(field, '<=', toValue),
            orderBy(field, 'desc')
        );
        const snapshot = await getDocs(q);
        snapshot.forEach(docSnapshot => {
            const data = docSnapshot.data();
            if (serviceMatches(data?.service, service)) {
                rowsById.set(docSnapshot.id, { ...data, id: docSnapshot.id, coll });
            }
        });
    }));

    return Array.from(rowsById.values());
}

const getMailStatus = (mail) => {
    if (!mail) return { label: 'Sin documento mail', status: 'missing', at: null, error: '' };
    const delivery = mail.delivery || {};
    const rawState = delivery.state || delivery.status || mail.state || mail.status || '';
    const state = `${rawState}`.toLowerCase();
    const error = delivery.error || mail.error || mail.lastError || '';
    const at = dateValue(delivery.endTime || delivery.sentAt || mail.sentAt || delivery.startTime || mail.updateTime);

    if (error || state.indexOf('error') > -1 || state.indexOf('fail') > -1) {
        return { label: 'Error email', status: 'error', at, error: typeof error === 'string' ? error : JSON.stringify(error) };
    }
    if (state.indexOf('success') > -1 || state.indexOf('sent') > -1 || state.indexOf('deliver') > -1) {
        return { label: 'Email enviado', status: 'sent', at, error: '' };
    }
    if (state.indexOf('process') > -1) return { label: 'Procesando email', status: 'processing', at, error: '' };
    if (state) return { label: rawState, status: 'unknown', at, error: '' };
    return { label: 'Email encolado', status: 'queued', at, error: '' };
}

const mailConfigs = {
    pap_alert_10_attempts: {
        idFields: ['stPapAlertMailId'],
        atFields: ['stPapAlertMailQueuedAt', 'stPapAlertMailAt']
    },
    pap_cancel_error: {
        idFields: ['stPapCancelMailId'],
        atFields: ['stPapCancelMailQueuedAt', 'stPapCancelMailAt']
    },
    no_policies_alert: {
        idFields: ['stNoPolAlertMailId'],
        atFields: ['stNoPolAlertMailQueuedAt']
    },
    no_policies_cancel: {
        idFields: ['stNoPolCancelMailId'],
        atFields: ['stNoPolCancelMailQueuedAt']
    }
};

const getFirstValue = (row, fields = []) => {
    const field = fields.find(name => row[name]);
    return field ? row[field] : null;
}

const getMailEntry = (row, mailType) => {
    if (!Array.isArray(row.stMails)) return null;
    return row.stMails.find(item => item?.type === mailType || item?.stMailType === mailType) || null;
}

const getEventMailId = (row, mailType) => {
    const config = mailConfigs[mailType] || {};
    const specificId = getFirstValue(row, config.idFields);
    if (specificId) return specificId;

    const mailEntry = getMailEntry(row, mailType);
    if (mailEntry?.id || mailEntry?.mailId || mailEntry?.stMailId) {
        return mailEntry.id || mailEntry.mailId || mailEntry.stMailId;
    }

    return row.stMailType === mailType ? row.stMailId : null;
}

const getEventMailAt = (row, mailType) => {
    const config = mailConfigs[mailType] || {};
    const specificAt = getFirstValue(row, config.atFields);
    if (specificAt) return specificAt;

    const mailEntry = getMailEntry(row, mailType);
    if (mailEntry?.queuedAt || mailEntry?.stMailQueuedAt || mailEntry?.at) {
        return mailEntry.queuedAt || mailEntry.stMailQueuedAt || mailEntry.at;
    }

    return row.stMailType === mailType ? row.stMailQueuedAt : null;
}

const lastArrayValue = value => Array.isArray(value) && value.length ? value[value.length - 1] : null;

const cancelEventAt = row => row.cancelId || row.cancelationTime || row.cancelledTime || row.cancelDate || row.alertId;

const alertMail10EventAt = row => (
    row.stPapAlertMailAt ||
    getEventMailAt(row, 'pap_alert_10_attempts') ||
    row.notificatedTime ||
    lastArrayValue(row.notificationTriesTimes) ||
    row.alertId
);

const collectMailIds = (rows) => Array.from(new Set(rows.flatMap(row => {
    const ids = [];
    Object.values(mailConfigs).forEach(config => {
        config.idFields.forEach(field => {
            if (row[field]) ids.push(row[field]);
        });
    });
    if (row.stMailId) ids.push(row.stMailId);
    if (Array.isArray(row.stMails)) {
        row.stMails.forEach(item => {
            const id = item?.id || item?.mailId || item?.stMailId;
            if (id) ids.push(id);
        });
    }
    return ids;
}).filter(Boolean)));

const buildEvent = ({ row, eventType, eventGroup, eventLabel, eventColor, eventAt, detail, mailType, mails }) => {
    const mailId = mailType ? getEventMailId(row, mailType) : null;
    const mail = mailId ? mails[mailId] : null;
    return {
        ...row,
        rowKey: `${row.rowKey}-${eventType}`,
        sourceRowKey: row.rowKey,
        eventType,
        eventGroup,
        eventLabel,
        eventColor,
        eventAt,
        detail: detail || '',
        eventMailType: mailType || '',
        eventMailId: mailId,
        eventMailQueuedAt: mailType ? getEventMailAt(row, mailType) : null,
        mail,
        mailStatus: mailType ? getMailStatus(mail) : null,
        hasMail: Boolean(mailType)
    };
}

const buildAlertStatEvents = (row, mails) => {
    const events = [];
    const add = event => events.push(buildEvent({ row, mails, ...event }));
    const hasAlertMail10 = row.stPapAlertMail || row.stMailType === 'pap_alert_10_attempts' || (row.notificationTries >= PAP_ALERT_MAX_NOTIFICATION_TRIES && row.mailed);
    const hasCancelMail = row.stPapCancelMail || row.stMailType === 'pap_cancel_error';
    const hasNoPolicyAlertMail = row.stNoPolAlertMail || row.stMailType === 'no_policies_alert' || row.mailedNoPoliciesAlert;
    const hasNoPolicyCancelMail = row.stNoPolCancelMail || row.stMailType === 'no_policies_cancel' || row.mailedNoPoliciesCancel;

    if (row.stPapAlertOk) add({
        eventType: 'papAlertOk',
        eventGroup: 'Alerta',
        eventLabel: 'Alerta PAP OK',
        eventColor: 'green',
        eventAt: row.stPapAlertAt || row.stPapAlertRespAt,
        detail: row.stPapAlertMsg
    });
    if (row.stPapAlertErr) add({
        eventType: 'papAlertErr',
        eventGroup: 'Alerta',
        eventLabel: 'Alerta PAP error',
        eventColor: 'red',
        eventAt: row.stPapAlertErrAt || row.stPapAlertRespAt,
        detail: row.stPapAlertErrMsg || row.stPapAlertMsg
    });
    if (hasAlertMail10) add({
        eventType: 'papAlertMail10',
        eventGroup: 'Email alerta',
        eventLabel: 'Aviso por mail tras 10 intentos',
        eventColor: 'orange',
        eventAt: alertMail10EventAt(row),
        mailType: 'pap_alert_10_attempts'
    });
    if (hasNoPolicyAlertMail) add({
        eventType: 'noPolicyAlertMail',
        eventGroup: 'Email alerta',
        eventLabel: 'Alerta sin polizas mail',
        eventColor: 'red',
        eventAt: getEventMailAt(row, 'no_policies_alert') || row.notificatedTime || row.alertId,
        mailType: 'no_policies_alert'
    });
    if (row.stCancelBeforePap) add({
        eventType: 'cancelBeforePap',
        eventGroup: 'Cancelacion',
        eventLabel: 'Cancelacion antes de PAP',
        eventColor: 'default',
        eventAt: cancelEventAt(row),
        detail: row.stPapCancelSkip
    });
    if (row.stPapCancelOk) add({
        eventType: 'papCancelOk',
        eventGroup: 'Cancelacion',
        eventLabel: 'Cancelacion PAP OK',
        eventColor: 'green',
        eventAt: row.stPapCancelAt || row.stPapCancelRespAt,
        detail: row.stPapCancelMsg
    });
    if (row.stPapCancelErr) add({
        eventType: 'papCancelErr',
        eventGroup: 'Cancelacion',
        eventLabel: 'Cancelacion PAP error',
        eventColor: 'red',
        eventAt: row.stPapCancelErrAt || row.stPapCancelRespAt,
        detail: row.stPapCancelErrMsg || row.stPapCancelMsg
    });
    if (hasCancelMail) add({
        eventType: 'papCancelMail',
        eventGroup: 'Email cancelacion',
        eventLabel: 'Mail error PAP cancelacion',
        eventColor: 'orange',
        eventAt: row.stPapCancelMailAt || getEventMailAt(row, 'pap_cancel_error'),
        mailType: 'pap_cancel_error'
    });
    if (hasNoPolicyCancelMail) add({
        eventType: 'noPolicyCancelMail',
        eventGroup: 'Email cancelacion',
        eventLabel: 'Cancelacion sin polizas mail',
        eventColor: 'red',
        eventAt: getEventMailAt(row, 'no_policies_cancel') || row.notificatedTime || cancelEventAt(row),
        mailType: 'no_policies_cancel'
    });
    if (row.stClosedBy) add({
        eventType: 'closed',
        eventGroup: 'Cierre',
        eventLabel: row.stClosedBy === 'auto' ? 'Cierre automatico' : 'Cierre manual',
        eventColor: row.stClosedBy === 'auto' ? 'blue' : 'purple',
        eventAt: row.closedTime || row.closeTime
    });

    if (!events.length) add({
        eventType: 'historical',
        eventGroup: 'Historico',
        eventLabel: 'Historico sin trazas',
        eventColor: 'default',
        eventAt: row.alertId
    });

    return events;
}

const isInRange = (value, from, to) => {
    const millis = dateValue(value);
    return millis != null && millis >= from && millis <= to;
}

const cancellationEventTypes = ['cancelBeforePap', 'papCancelOk', 'papCancelErr', 'papCancelMail', 'noPolicyCancelMail'];
const alertPathEventTypes = ['papAlertOk', 'papAlertErr', 'papAlertMail10', 'noPolicyAlertMail'];
const cancellationPathEventTypes = ['cancelBeforePap', 'papCancelOk', 'papCancelErr', 'papCancelMail', 'noPolicyCancelMail'];

const hasAlertFlowTrace = row => Boolean(
    row.stPapAlertOk ||
    row.stPapAlertErr ||
    row.stPapAlertMail ||
    row.stNoPolAlertMail ||
    row.mailedNoPoliciesAlert ||
    row.notificationTries > 0 ||
    (Array.isArray(row.notificationTriesTimes) && row.notificationTriesTimes.length)
);

const isPendingPureCancellation = row => Boolean(
    row.coll === 'alerts' &&
    row.cancelled &&
    !row.notificated &&
    !hasAlertFlowTrace(row)
);

const isPureCancelBeforePap = row => (row.stCancelBeforePap || isPendingPureCancellation(row)) && !hasAlertFlowTrace(row);

const hasAnyAlertStatsTrace = row => Object.keys(row || {}).some(key => key.startsWith('st') && row[key]);

const isPendingAlertValidation = row => {
    if (row.coll !== 'alerts') return false;
    if (row.notificated || hasAlertFlowTrace(row) || hasAnyAlertStatsTrace(row)) return false;

    const alertAt = dateValue(row.alertId);
    return alertAt != null && Date.now() - alertAt <= ALERT_VALIDATION_GRACE_MS;
}

const eventKeySet = (rows, predicate) => new Set(rows.filter(predicate).map(alertStatsKey));

const isFallbackEvent = row => (
    row.eventType === 'historical' ||
    (row.eventType === 'papAlertMail10' && !row.stPapAlertMail) ||
    (row.eventType === 'noPolicyAlertMail' && !row.stNoPolAlertMail) ||
    (row.eventType === 'noPolicyCancelMail' && !row.stNoPolCancelMail)
);

const isStrongFallbackEvent = row => (
    (row.eventType === 'papAlertMail10' && !row.stPapAlertMail && row.notificationTries >= PAP_ALERT_MAX_NOTIFICATION_TRIES && row.mailed) ||
    (row.eventType === 'noPolicyAlertMail' && !row.stNoPolAlertMail && row.mailedNoPoliciesAlert) ||
    (row.eventType === 'noPolicyCancelMail' && !row.stNoPolCancelMail && row.mailedNoPoliciesCancel)
);

const hasSpecificMailTrace = row => {
    const config = mailConfigs[row.eventMailType] || {};
    return Boolean(row.eventMailId && getFirstValue(row, config.idFields));
}

const getAlertStatsReportFromDb = async (db, params) => {
    const report = await getAlertPapReportFromDb(db, params);
    const { from, to } = params;
    const events = report.rows || [];
    const documents = report.documents || [];
    const alertCohortKeys = new Set(
        documents
            .filter(row => isInRange(row.alertId, from, to) && !isPureCancelBeforePap(row))
            .map(alertStatsKey)
    );
    const alertCohortEvents = events.filter(row =>
        alertPathEventTypes.includes(row.eventType) && alertCohortKeys.has(alertStatsKey(row))
    );

    const alertIncidents = eventKeySet(alertCohortEvents, row =>
        alertPathEventTypes.includes(row.eventType) && row.stPapAlertErr
    );
    const alertIncidentsResolved = eventKeySet(alertCohortEvents, row =>
        alertPathEventTypes.includes(row.eventType) && row.stPapAlertErr && row.stPapAlertOk
    );
    const alertIncidentsUnresolved = eventKeySet(alertCohortEvents, row =>
        alertPathEventTypes.includes(row.eventType) && row.stPapAlertErr && !row.stPapAlertOk
    );
    const alertNoPap = eventKeySet(alertCohortEvents, row =>
        alertPathEventTypes.includes(row.eventType) && (
            row.eventType === 'papAlertMail10' ||
            row.eventType === 'noPolicyAlertMail' ||
            (row.stPapAlertErr && !row.stPapAlertOk)
        )
    );
    const alertResolved = eventKeySet(alertCohortEvents, row =>
        alertPathEventTypes.includes(row.eventType) && (
            row.eventType === 'papAlertOk' ||
            row.eventType === 'papAlertMail10' ||
            row.eventType === 'noPolicyAlertMail'
        )
    );
    const cancelIncidents = eventKeySet(events, row =>
        cancellationPathEventTypes.includes(row.eventType) && row.stPapCancelErr
    );
    const cancelIncidentsResolved = eventKeySet(events, row =>
        cancellationPathEventTypes.includes(row.eventType) && row.stPapCancelErr && row.stPapCancelOk
    );
    const cancelIncidentsUnresolved = eventKeySet(events, row =>
        cancellationPathEventTypes.includes(row.eventType) && row.stPapCancelErr && !row.stPapCancelOk
    );
    const cancelNoPap = eventKeySet(events, row =>
        cancellationPathEventTypes.includes(row.eventType) && (
            row.eventType === 'cancelBeforePap' ||
            row.eventType === 'papCancelMail' ||
            row.eventType === 'noPolicyCancelMail' ||
            (row.stPapCancelErr && !row.stPapCancelOk)
        )
    );
    const manualClosures = eventKeySet(events, row => row.eventType === 'closed' && row.stClosedBy === 'manual');
    const autoClosures = eventKeySet(events, row => row.eventType === 'closed' && row.stClosedBy === 'auto');
    const eventsWithMail = events.filter(row => row.hasMail);
    const eventsWithSpecificMail = eventsWithMail.filter(hasSpecificMailTrace);
    const eventsWithGenericMail = eventsWithMail.filter(row => row.eventMailId && !hasSpecificMailTrace(row));
    const eventsWithoutMailDoc = eventsWithMail.filter(row => row.mailStatus?.status === 'missing');
    const fallbackEvents = events.filter(row => isFallbackEvent(row) && row.eventType !== 'historical');
    const historicalEvents = events.filter(row => row.eventType === 'historical');
    const strongFallbackEvents = events.filter(isStrongFallbackEvent);
    const stFlagEvents = events.filter(row => !isFallbackEvent(row));
    const reliabilityPct = events.length ? Math.round((stFlagEvents.length / events.length) * 100) : 0;

    const summary = {
        alerts: {
            generated: alertCohortKeys.size,
            active: report.summary?.active || 0,
            papOk: alertCohortEvents.filter(row => row.eventType === 'papAlertOk').length,
            noPap: alertNoPap.size,
            resolved: alertResolved.size,
            papIncidents: alertIncidents.size,
            papIncidentsResolved: alertIncidentsResolved.size,
            papIncidentsUnresolved: alertIncidentsUnresolved.size,
            mailAfterTenTries: alertCohortEvents.filter(row => row.eventType === 'papAlertMail10').length,
            noPolicies: alertCohortEvents.filter(row => row.eventType === 'noPolicyAlertMail').length
        },
        cancellations: {
            total: report.summary?.cancelled || 0,
            beforePap: report.summary?.cancelBeforePap || 0,
            papOk: report.summary?.papCancelOk || 0,
            noPap: cancelNoPap.size,
            papIncidents: cancelIncidents.size,
            papIncidentsResolved: cancelIncidentsResolved.size,
            papIncidentsUnresolved: cancelIncidentsUnresolved.size,
            papErrorMail: report.summary?.papCancelMail || 0,
            noPolicies: report.summary?.noPolicyCancelMail || 0
        },
        closures: {
            total: report.summary?.closed || 0,
            manual: manualClosures.size,
            auto: autoClosures.size
        },
        mails: {
            traced: report.summary?.mailsQueued || 0,
            sent: report.summary?.mailsSent || 0,
            error: report.summary?.mailsError || 0,
            pending: report.summary?.mailsPending || 0,
            missingDoc: eventsWithoutMailDoc.length,
            specificId: eventsWithSpecificMail.length,
            genericId: eventsWithGenericMail.length
        },
        quality: {
            totalEvents: events.length,
            reliabilityPct,
            stFlags: stFlagEvents.length,
            fallback: fallbackEvents.length,
            strongFallback: strongFallbackEvents.length,
            historical: historicalEvents.length,
            specificMailId: eventsWithSpecificMail.length,
            genericMailId: eventsWithGenericMail.length
        }
    };

    const comparisons = [
        {
            key: 'alertPapOk',
            group: 'Alertas',
            label: 'PAP OK vs alertas generadas',
            value: summary.alerts.papOk,
            total: summary.alerts.generated
        },
        {
            key: 'alertNoPap',
            group: 'Alertas',
            label: 'No PAP vs alertas generadas',
            value: summary.alerts.noPap,
            total: summary.alerts.generated
        },
        {
            key: 'alertResolved',
            group: 'Alertas',
            label: 'Resueltas vs alertas generadas',
            value: summary.alerts.resolved,
            total: summary.alerts.generated
        },
        {
            key: 'cancelPapOk',
            group: 'Cancelaciones',
            label: 'PAP OK vs cancelaciones',
            value: summary.cancellations.papOk,
            total: summary.cancellations.total
        },
        {
            key: 'cancelNoPap',
            group: 'Cancelaciones',
            label: 'No PAP vs cancelaciones',
            value: summary.cancellations.noPap,
            total: summary.cancellations.total
        }
    ];

    return {
        summary,
        comparisons,
        events,
        documents
    };
}

const getAlertPapReportFromDb = async (db, { service, from, to, includeMail = true }) => {
    const [alerts, cancelled, closed] = await Promise.all([
        queryAlertStatsCollection(db, { coll: 'alerts', from, to, service }),
        queryAlertStatsCollection(db, { coll: 'cancelled', from, to, service }),
        queryAlertStatsCollection(db, { coll: 'closed', from, to, service })
    ]);
    const rows = [...alerts, ...cancelled, ...closed];
    const mailIds = collectMailIds(rows);
    let mails = {};

    if (includeMail && mailIds.length) {
        const mailDocs = await Promise.all(mailIds.map(async mailId => {
            const mailSnapshot = await getDoc(doc(db, 'mail', mailId.toString()));
            return [mailId, mailSnapshot.exists() ? mailSnapshot.data() : null];
        }));
        mails = Object.fromEntries(mailDocs);
    }

    const detailedRows = rows.map(row => ({
        ...row,
        rowKey: `${row.coll}-${row.id || row.alertId || ''}-${row.phone || ''}`
    }));
    const stableRows = detailedRows.filter(row => !isPendingAlertValidation(row));
    const allEventRows = stableRows
        .flatMap(row => buildAlertStatEvents(row, mails))
    const eventRows = allEventRows
        .filter(row => isInRange(row.eventAt, from, to))
        .sort((a, b) => (dateValue(b.eventAt) || Number(b.alertId || 0)) - (dateValue(a.eventAt) || Number(a.alertId || 0)));
    const generatedKeys = new Set(
        stableRows
            .filter(row => isInRange(row.alertId, from, to) && !isPureCancelBeforePap(row))
            .map(alertStatsKey)
    );
    const cancelledKeys = new Set(
        eventRows
            .filter(row => cancellationEventTypes.includes(row.eventType) || (row.coll === 'cancelled' && row.eventType === 'historical'))
            .map(alertStatsKey)
    );
    const closedKeys = new Set(
        eventRows
            .filter(row => row.eventType === 'closed' || (row.coll === 'closed' && row.eventType === 'historical'))
            .map(alertStatsKey)
    );

    const summary = {
        generated: generatedKeys.size,
        active: stableRows.filter(row => row.coll === 'alerts' && isInRange(row.alertId, from, to)).length,
        cancelled: cancelledKeys.size,
        closed: closedKeys.size,
        papAlertOk: eventRows.filter(row => row.eventType === 'papAlertOk').length,
        papAlertErr: eventRows.filter(row => row.eventType === 'papAlertErr').length,
        papAlertMail10: eventRows.filter(row => row.eventType === 'papAlertMail10').length,
        noPolicyAlertMail: eventRows.filter(row => row.eventType === 'noPolicyAlertMail').length,
        cancelBeforePap: eventRows.filter(row => row.eventType === 'cancelBeforePap').length,
        papCancelOk: eventRows.filter(row => row.eventType === 'papCancelOk').length,
        papCancelErr: eventRows.filter(row => row.eventType === 'papCancelErr').length,
        papCancelMail: eventRows.filter(row => row.eventType === 'papCancelMail').length,
        noPolicyCancelMail: eventRows.filter(row => row.eventType === 'noPolicyCancelMail').length,
        mailsQueued: eventRows.filter(row => row.hasMail).length,
        mailsSent: eventRows.filter(row => row.hasMail && row.mailStatus?.status === 'sent').length,
        mailsError: eventRows.filter(row => row.hasMail && row.mailStatus?.status === 'error').length,
        mailsPending: eventRows.filter(row => row.hasMail && !['sent', 'error'].includes(row.mailStatus?.status)).length
    };

    return { rows: eventRows, summary, documents: stableRows };
}

export {
    getAlertPapReportFromDb,
    getAlertStatsReportFromDb
};
